import React from "react";
import { Column, Conditional, Else, Grid, Heading, If, Row, Section, TruncatedText } from "@hex-insights/core";
import { useActivePageRegistration } from "@hex-insights/router";
import {
	ButtonLink,
	ClassYearFormat,
	ClassYearNameFragment,
	ClassYearUtilities,
	ContractInstallmentComponents,
	ContractLineItemFormat,
	ContractLineItemUtilities,
	ContractUtilities,
	Icon,
	PersonFormat,
	ReEnrollmentReviewQuery,
	ServiceCategory,
	ServiceFormat,
	StudentComponents,
	StudentFormat,
	StudentUtilities,
	useReEnrollmentReviewQuery,
} from "@hex-insights/verita-romania.shared";
import { ReEnrollmentLayout } from "../Components";
import { reEnrollmentPaymentPlansPageInfo } from "../PaymentPlansPage/pageinfo";
import { reEnrollmentSuccessPageInfo } from "../SuccessPage/pageinfo";
import { getReEnrollmentContractInStudentFilter, getReEnrollmentStudentFilter } from "../Utilities";
import { reEnrollmentReviewPageInfo } from "./pageinfo";
import generalStyles from "../general.module.css";
import styles from "./styles.module.css";

const responsiveGridTracks = {
	0: 1,
	700: 2,
};

export function ReEnrollmentReviewPage() {
	useActivePageRegistration(reEnrollmentReviewPageInfo);

	const { loading, data } = useReEnrollmentReviewQuery({
		variables: {
			studentFilters: getReEnrollmentStudentFilter(),
			contractFilters: getReEnrollmentContractInStudentFilter(),
		},
		fetchPolicy: "network-only",
	});
	const { totalPrice, contractInstallments, any4Payments, any9Payments } = React.useMemo(() => {
		if (!data) {
			return { totalPrice: 0, contractInstallments: [], any4Payments: false, any9Payments: false };
		}

		let totalPrice = 0;
		const contracts = [];
		let any4Payments = false;
		let any9Payments = false;
		for (let i = 0; i < data.studentConnection.edges.length; i++) {
			const student = data.studentConnection.edges[i].node;
			for (let j = 0; j < student.contracts.length; j++) {
				const contract = student.contracts[j];
				contracts.push(contract);
				totalPrice += ContractUtilities.calculateTotalPrice(contract);
				if (contract.contractInstallments.length === 4) {
					any4Payments = true;
				}
				if (contract.contractInstallments.length === 9) {
					any9Payments = true;
				}
			}
		}
		const contractInstallments = ContractUtilities.mergeContractInstallments(contracts);

		return { totalPrice, contractInstallments, any4Payments, any9Payments };
	}, [data]);

	const { classYears } = ClassYearUtilities.useClassYearsMap();

	return (
		<ReEnrollmentLayout
			footerContent={
				<Row justify="space-between" align="center">
					<ButtonLink variant="link" to={reEnrollmentPaymentPlansPageInfo.to}>
						<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
							<Icon.ArrowLeft size={24} /> Back
						</Row>
					</ButtonLink>

					<ButtonLink variant="primary" to={reEnrollmentSuccessPageInfo.to}>
						Submit
					</ButtonLink>
				</Row>
			}
		>
			<div className={styles["main-content"]}>
				<Heading.H2 className={generalStyles["heading"]}>
					Please review the information below before submitting.
				</Heading.H2>

				<Conditional>
					<If condition={loading && !data}>
						<div>Loading...</div>
					</If>
					<Else>
						<Section>
							<Section.Header>
								<Heading.H4 noMargin className={generalStyles["heading"]}>
									Students
								</Heading.H4>
							</Section.Header>
							<Section.Body>
								<Grid responsiveTracks={responsiveGridTracks} gap="1.5rem">
									{data?.studentConnection.edges.map(({ node: student }) => (
										<ReviewStudent key={student.id} student={student} classYears={classYears} />
									))}
								</Grid>
							</Section.Body>
						</Section>

						<Section>
							<Section.Header>
								<Heading.H4 noMargin className={generalStyles["heading"]}>
									Payment Plan
								</Heading.H4>
							</Section.Header>
							<Section.Body>
								<ContractInstallmentComponents.ContractInstallmentsTable
									totalPrice={totalPrice}
									contractInstallments={contractInstallments}
									hidePortion
								/>

								<If condition={any4Payments}>
									<span className={styles["payment-plan__fee-notice"]}>
										Since you have selected paying in 4 payments, a 2% surcharge has been applied to the tuition and
										meal plan.
									</span>
								</If>

								<If condition={any9Payments}>
									<span className={styles["payment-plan__fee-notice"]}>
										Since you have selected paying in 9 payments, a 5% surcharge has been applied to the tuition and
										meal plan.
									</span>
								</If>
							</Section.Body>
						</Section>
					</Else>
				</Conditional>
			</div>
		</ReEnrollmentLayout>
	);
}

type ReviewStudentProps = {
	student: ReEnrollmentReviewQuery["studentConnection"]["edges"][0]["node"];
	classYears: Record<number, ClassYearNameFragment>;
};

function ReviewStudent({ student, classYears }: ReviewStudentProps) {
	const { tuitionAmount, mealPlan, mealPlanAmount } = React.useMemo(() => {
		let tuition = null;
		let mealPlan = null;
		for (let i = 0; i < student.contracts.length; i++) {
			const contract = student.contracts[i];
			for (let j = 0; j < contract.contractLineItems.length; j++) {
				const lineItem = contract.contractLineItems[j];
				if (tuition === null && lineItem.service?.category === ServiceCategory.Tuition) {
					tuition = lineItem;
				}
				if (mealPlan === null && lineItem.service?.category === ServiceCategory.MealPlan) {
					mealPlan = lineItem;
				}
			}
		}

		const tuitionAmount = tuition ? ContractLineItemUtilities.calculateTotalAmount(tuition) : 0;
		const mealPlanAmount = mealPlan ? ContractLineItemUtilities.calculateTotalAmount(mealPlan) : 0;

		return { tuition, tuitionAmount, mealPlan, mealPlanAmount };
	}, [student]);

	const nextClassYear = StudentUtilities.getNextClassYear(student, classYears);

	return (
		<Column justify="spaced-start" verticalSpacing="1rem" className={styles["student-card"]}>
			<Row justify="spaced-start" align="center" horizontalSpacing="1rem" style={{ minWidth: 0 }}>
				<StudentComponents.StudentIcon student={student} size={64} />

				<Column justify="spaced-start" verticalSpacing="0.25rem" style={{ minWidth: 0 }}>
					<TruncatedText className={styles["student-card__name"]} style={{ minWidth: 0 }}>
						{StudentFormat.name(student)}
					</TruncatedText>
					<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem">
						<Icon.PersonArmsSpread size={16} color="var(--neutral-colors--tertiary)" />
						<TruncatedText className={styles["student-card__detail__text"]}>
							{PersonFormat.age(student.person.dateOfBirth)}
						</TruncatedText>
					</Row>
				</Column>
			</Row>

			<Column justify="spaced-start" verticalSpacing="0.25rem">
				<span className={styles["student-card__item__label"]}>Tuition</span>
				<Row justify="spaced-start" horizontalSpacing="0.5rem" className={styles["student-card__item__value"]}>
					{nextClassYear && (
						<React.Fragment>
							<Icon.GraduationCap size={16} />
							<span>{ClassYearFormat.name(nextClassYear)}</span>
							<span>-</span>
						</React.Fragment>
					)}
					<span className={styles["student-card__item__value__amount"]}>
						{ContractLineItemFormat.Fields.amount(tuitionAmount)}
					</span>
				</Row>
			</Column>

			<Column justify="spaced-start" verticalSpacing="0.25rem">
				<span className={styles["student-card__item__label"]}>Meal Plan</span>
				<Row justify="spaced-start" horizontalSpacing="0.5rem" className={styles["student-card__item__value"]}>
					<Icon.ForkKnife size={16} />
					<span>{mealPlan?.service ? ServiceFormat.name(mealPlan.service) : "None"}</span>
					<span>-</span>
					<span className={styles["student-card__item__value__amount"]}>
						{ContractLineItemFormat.Fields.amount(mealPlanAmount)}
					</span>
				</Row>
			</Column>
		</Column>
	);
}
